import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { MandatorizeColumns } from './helpers/MandatorizeColumns';
import { stumbleOnMissingMandatoryColumn } from './helpers/stumbleOnMissingMandatoryColumn';

/**
 * In some custom table queries we assume that some columns
 * have always filled data. To ease type checking,
 * this hook can be used to validate that those columns
 * contain neither null nor undefined and narrow the result type.
 *
 * This variant is useful together with useQuery or its wrapped variants (e.g. useTableFooBarBaz... from api-client).
 *
 * @see validateMandatoryColumns as a variant for direct API calls.
 * @example
 * const queryResult = usePostTablesManagementConfiguration(
 *   {
 *     columns: ['hash', 'lastChangeAt', 'hostname', 'sn'],
 *     filters: {sn: ['eq', '12345]},
 *   }
 * );
 * return useTableQueryWithMandatoryColumns(
 *   ['hash', 'lastChangeAt', 'hostname', 'sn'],
 *   queryResult,
 * );
 */
export const useTableQueryWithMandatoryColumns = <
  TData extends { data: { [key: string]: unknown }[] },
  TError,
  TMandatoryColumn extends keyof TData['data'][number] & string,
>(
  mandatoryColumns: TMandatoryColumn[],
  queryResult: UseQueryResult<TData, TError>,
): UseQueryResult<
  Omit<TData, 'data'> & {
    data: MandatorizeColumns<TData['data'][number], TMandatoryColumn>[];
  },
  TError
> => {
  // biome-ignore lint/correctness/useExhaustiveDependencies(mandatoryColumns): expected stable content
  useMemo(() => {
    if (queryResult.data === undefined) {
      return;
    }
    stumbleOnMissingMandatoryColumn(queryResult.data.data, mandatoryColumns);
  }, [queryResult.data]);
  // type compatibility is checked by the useMemo above
  return queryResult as any;
};
